<template lang="pug">
.calendar(:data-size="resize.observer ? resize.size : false")
  full-calendar(ref="fullCalendar", :options="calendarOptions")
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";

import media from "./mixins/media";

export default {
  name: "CalendarApp",
  mixins: [media],
  components: {
    FullCalendar,
  },
  data() {
    return {
      activePreview: null,
      activeDetail: null,
      error: false,
      calendar: null,
      overrides: {},
      calendarOptions: {
        height: "auto",
        events: [],
        plugins: [dayGridPlugin, listPlugin, interactionPlugin],
        initialView: "dayGridMonth",
        eventClick: this.eventClick,
        eventMouseEnter: this.eventMouseEnter,
        eventMouseLeave: this.eventMouseLeave,
        dayHeaderFormat: { weekday: "long" },
        buttonText: {
          today: "today",
          month: "month",
          week: "week",
          day: "day",
          listMonth: "list",
          listOverflow: "list"
        },
        headerToolbar: {
          start: "title",
          center: "today prev,next",
          end: "dayGridMonth,dayGridWeek,dayGridDay,listOverflow",
        },
        eventTimeFormat: {
          hour: "numeric",
          minute: "2-digit",
          meridiem: "short",
        },
        views: {
          listOverflow: {
            type: 'list',
            listDaySideFormat: {weekday: 'long'},
            visibleRange: function(currentDate) {
              var startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
              // zeroth'd month value so + 2 to push up a month ahead
              var endDate = new Date(currentDate.getFullYear(), currentDate.getMonth()+2);
              // Adjust the start & end dates, respectively
              startDate.setDate(startDate.getDate());
              endDate.setDate(endDate.getDate()); 
              return { start: startDate, end: endDate };
            },
            dateIncrement: { month: 1 },
            buttonText: 'list'
          },
        }
      },
    };
  },
  computed: {
    api() {
      return this.$root.api;
    },
    isListView() {
      return this.calendar.view.type.toLowerCase().includes("list");
    }
  },
  methods: {
    getData() {
      return new Promise((resolve) => {
        fetch(this.api)
          .then((res) => res.json())
          .then((data) => {
            this.overrides = data.overrides;
            this.calendarOptions.events = data.events;
          })
          .catch((error) => {
            console.error(error);
            this.error = true;
          })
          .finally(resolve);
      });
    },
    setLargeView() {
      this.calendar.changeView(this.overrides.initialView_large);
      this.calendarOptions.buttonText = {
        today: "today",
        month: "month",
        week: "week",
        day: "day",
        listMonth: "list",
        listOverflow: "list"
      };
      this.calendarOptions.headerToolbar = {
        start: "title",
        center: "dayGridMonth,dayGridWeek,dayGridDay,listOverflow",
        end: "today prev,next",
      };
    },
    setSmallView() {
      this.calendar.changeView(this.overrides.initialView_small);
      this.calendarOptions.buttonText = {
        today: "today",
        listMonth: "month",
        listWeek: "week",
        listDay: "day",
        listOverflow: "month"
      };
      this.calendarOptions.headerToolbar = {
        start: "title",
        end: "today listOverflow,listWeek,listDay,prev,next",
      };
    },
    setView() {
      if (!this.resize.observer) return;

      const size = this.resize.size;
      if (size === "XL" || size === "LG" || size === "MD") {
        this.setLargeView();
      } else {
        this.setSmallView();
      }
    },
    createPreview(event) {
      let preview = (this.activePreview = document.createElement("div"));
      let inner = document.createElement("div");
      preview.classList = "event__preview";
      preview.insertAdjacentElement("afterbegin", inner);
      inner.insertAdjacentHTML(
        "afterbegin",
        `<h6>${event.extendedProps.heading}</h6>`
      );
      return preview;
    },
    createDetail(event) {
      let detail = (this.activeDetail = document.createElement("div"));
      let inner = document.createElement("div");
      detail.classList = "event__detail";
      detail.insertAdjacentElement("afterbegin", inner);
      inner.insertAdjacentHTML(
        "afterbegin",
        `<h6>${event.extendedProps.heading}</h6>`
      );
      inner.insertAdjacentHTML(
        "beforeend",
        `<p>${event.extendedProps.copy}</p>`
      );
      return detail;
    },
    removeTooltips() {
      let status = { preview: false, detail: false };
      if (this.activePreview) {
        this.activePreview.remove();
        this.activePreview = null;
        status.preview = true;
      }
      if (this.activeDetail) {
        this.activeDetail.remove();
        this.activeDetail = null;
        status.detail = true;
      }
      return status;
    },
    eventClick(args) {
      args.jsEvent.preventDefault();
      if (this.isListView) {
        if(args.event.url) {
          // catching null and undefined specifically to allow new window as the default
          if (args.event.extendedProps.openInNewWindow || args.event.extendedProps.openInNewWindow === undefined || args.event.extendedProps.openInNewWindow === null ) {
            window.open(args.event.url, "_blank")
          } else {
            window.open(args.event.url, "_self")
          }
        } else {
          false
        }
        return;
      }

      let alreadyOpen = this.removeTooltips().detail;
      if (alreadyOpen) return;

      let detail = this.createDetail(args.event);
      if(args.event.url) {
        detail.addEventListener('click', () => {
          // catching null and undefined specifically to allow new window as the default
          if (args.event.extendedProps.openInNewWindow || args.event.extendedProps.openInNewWindow === undefined || args.event.extendedProps.openInNewWindow === null ) {
            window.open(args.event.url, "_blank")
          } else {
            window.open(args.event.url, "_self")
          }
          }
        )
      } else {
        false
      }
      args.el.insertAdjacentElement("beforeend", detail);
    },
    eventMouseEnter(args) {
      if (this.isListView) return;

      this.removeTooltips();

      let preview = this.createPreview(args.event);
      args.el.insertAdjacentElement("beforeend", preview);
    },
    eventMouseLeave() {
      this.removeTooltips();
    },
		addintroText() {
			if (this.$root.introText && this.$root.introText.length > 0) {
				let parent = this.$refs.fullCalendar.$el;
				let h2 = parent.querySelector("h2.fc-toolbar-title");
				let p = document.createElement("p");
				p.classList.add("fc-intro-text");
				p.textContent = this.$root.introText;
				h2.insertAdjacentElement('afterend', p);
			}
		}
  },
  watch: {
    "resize.size"() {
      this.setView();
    },
  },
  async created() {
    await this.getData();
    this.resize_init();
  },
  mounted() {
    this.calendar = this.$refs.fullCalendar.getApi();
		this.addintroText();
  },
};
</script>

<style lang="scss">
.calendar {
  margin-bottom: 2rem;
  &[data-size="XS"],
  &[data-size="SM"],
  &[data-size="MD"] {
    .fc .fc-header-toolbar {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
    }
    .fc .fc-toolbar-chunk {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      width: 100%;
      .fc-toolbar-title {
        width: 100%;
        text-align: left;
      }
    }
		.fc p.fc-intro-text {
			margin-bottom: 1rem;
		}
    .fc .fc-button-group {
      margin-left: 0;
      margin-top: 0.75rem;
    }
  }
	&[data-size="MD"] {
		.fc-toolbar-chunk:last-child {
			margin-top: 1.125rem;
		}
	}
	&[data-size="LG"],
	&[data-size="XL"] {
		.fc .fc-toolbar-chunk {
			width: 33.33%;
			flex: 0 0 33.33%;
			max-width: 33.33%;
			&:nth-child(2) {
				display: flex;
				justify-content: center;
			}
			&:last-child {
				display: flex;
				justify-content: flex-end;
			}
		}
	}

  .fc .fc-toolbar-title {
    @include fluid(font-size, 1.5rem, 2.125rem);
    display: flex;
    flex-direction: column;
    margin-bottom: 1.25rem;
    text-align: left;
  }

	.fc p.fc-intro-text {
		margin: 0;
		font-weight: 700;
		@include fluid(font-size, 0.875rem, 1rem);
	}

  .fc .fc-scroller-harness {
    overflow: visible;
  }

  .fc .fc-list-event-time {
    white-space: normal;
    width: 35%;
    padding-right: 0;
  }

  .fc .fc-daygrid-body {
    z-index: 20;
  }

  .fc-day {
    a {
      color: inherit;
      cursor: default;
      &:hover {
        color: inherit;
        text-decoration: none;
      }
    }
  }

  .fc-list-event,
  .fc-daygrid-event-harness a {
    cursor: pointer;
    position: relative;
  }

  .fc-col-header-cell {
    &.fc-day {
      background: $teal;
      color: $white;
      padding: 0.25rem;
    }
  }

  .fc-daygrid-day {
    &.fc-day {
      color: $teal;
      font-weight: 700;
      font-size: 1.125rem;
    }
  }

  .fc-daygrid-event {
    border-radius: 0;
    padding: 0.25rem 0.125rem 0.125rem;
    border: none;
  }

  div.event__preview,
  div.event__detail {
    display: flex;
    left: 0.25rem;
    bottom: 100%;
    position: absolute;
    padding-bottom: 0.75rem;
    z-index: 10;
    &::after {
      position: absolute;
      top: calc(100% - 0.75rem);
      left: 0;
      height: 0;
      width: 0;
      border-top: 8px solid $gray;
      border-right: 5px solid transparent;
      content: "";
    }
    div {
      display: flex;
      flex-direction: column;
      box-shadow: -0.125rem 0.125rem 15px rgba($black, 0.35), 0 0 40px rgba($black, 0.05);
      background: $gray;
      padding: 0.75rem;
      max-width: 100%;
      white-space: normal;
    }
    h6 {
      margin: 0 0 0.25rem;
      font-size: 1rem;
      font-weight: 400;
      &:only-child {
        margin: 0;
      }
    }
    p {
      margin: 0;
      font-weight: 300;
      font-size: 0.875rem;
      color: $fontColorBase;
    }
  }
}
</style>